import { useState, useEffect } from 'react';
import SidebarProfile from '../../../../shared/components/SidebarProfile';
import { flushSync } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../../../components/common/Logo/Logo';
import { C, t } from './dashboardConstants';
import { IcoLogout } from './DashboardIcons';
import { useAuth } from '../../../../contexts/AuthContext';
import { getMyTalentProfile } from '../../../../api/talentApi';
import { getMyProducerProfile } from '../../../../api/producerApi';
import { getDefaultProfileImage, isTalentProfileImageRemoved, resolveProfileImageUrl } from '../../../../shared/utils/profilePlaceholders';

const ROLE_LABEL = { TALENT: '배우', PRODUCER: '제작자' };

const MENU = [
  { id: 'dashboard', label: '대시보드', path: '/talent/dashboard', iconSrc: '/assets/images/dashboard.svg' },
  { id: 'portfolio', label: '나의 포트폴리오', path: '/talent/profile', iconSrc: '/assets/images/portfolio.svg' },
  { id: 'applications', label: '지원현황', path: '/talent/applications', iconSrc: '/assets/images/application.svg' },
  { id: 'archive', label: '보관함', path: '/talent/archive', iconSrc: '/assets/images/board.svg' },
  { id: 'messages', label: '메시지', path: '/talent/messages', iconSrc: '/assets/images/message.svg', comingSoon: true },
  { id: 'schedule', label: '일정 관리', path: '/talent/schedule', iconSrc: '/assets/images/calendar.svg' },
  { id: 'settings', label: '계정 설정', path: '/talent/settings', iconSrc: '/assets/images/settings.svg' },
];

function MenuIcon({ src, color }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: '24px',
        height: '24px',
        flexShrink: 0,
        display: 'block',
        backgroundColor: color,
        WebkitMaskImage: `url(${src})`,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskImage: `url(${src})`,
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: 'contain',
      }}
    />
  );
}


function LogoutConfirmModal({ onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fcfcfd', borderRadius: '20px', padding: '36px 40px', width: '360px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#090909', marginBottom: '8px' }}>로그아웃</div>
          <div style={{ fontSize: '15px', fontWeight: 400, color: '#888896' }}>로그아웃 하시겠습니까?</div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onCancel} style={{ flex: 1, height: '48px', borderRadius: '12px', border: '1px solid #E9E9EC', background: '#fcfcfd', cursor: 'pointer', fontSize: '15px', fontWeight: 600, color: '#090909' }}>취소</button>
          <button onClick={onConfirm} style={{ flex: 1, height: '48px', borderRadius: '12px', border: 'none', background: '#090909', cursor: 'pointer', fontSize: '15px', fontWeight: 600, color: '#fff' }}>로그아웃</button>
        </div>
      </div>
    </div>
  );
}

function Sidebar({ open, onClose }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isLoggedIn, role, isProducer, logout, userName, userStageName, userProfileImage, setUserInfo, setProfile, switchRole } = useAuth();

  // role이 TALENT로 세팅될 때마다 배우 프로필 갱신
  useEffect(() => {
    if (!isLoggedIn || role !== 'TALENT') return;
    if (isTalentProfileImageRemoved()) {
      setUserInfo({ profileImage: null });
      return;
    }
    getMyTalentProfile()
      .then(p => {
        setProfile(p.id);
        setUserInfo({
          name: p.stageName || p.name,
          profileImage: resolveProfileImageUrl(p) || null,
          stageName: p.stageName || null,
          realName: p.name || null,
        });
      })
      .catch(() => {});
  }, [isLoggedIn, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSwitchToProducer = () => {
    flushSync(() => { switchRole('PRODUCER'); });
    navigate('/producer/dashboard');
  };
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const activeId = MENU.find(m => pathname.startsWith(m.path))?.id ?? 'dashboard';
  const talentDefaultProfileImage = getDefaultProfileImage('TALENT');
  const resolvedTalentProfileImage =
    (!isTalentProfileImageRemoved() && userProfileImage) ? userProfileImage : talentDefaultProfileImage;
  const isTalentDefaultProfileImage = resolvedTalentProfileImage === talentDefaultProfileImage;

  const sidebarStyle = {
    position: 'fixed', left: 0, top: 0, height: '100vh',
    background: C.bg, borderRight: `1px solid ${C.g200}`,
    display: 'flex', flexDirection: 'column', zIndex: 200,
    transition: 'transform 0.25s ease',
    width: 'clamp(240px, 25vw, 342px)',
  };

  return (
    <>
      {open && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 199 }} />}
      <aside className={`sidebar-drawer${open ? ' sidebar-open' : ''}`} style={sidebarStyle}>
      <div className="sidebar-logo" onClick={() => navigate('/')}>
        <Logo />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <SidebarProfile
          src={resolvedTalentProfileImage}
          name={userStageName || userName}
          role={ROLE_LABEL[role] || role}
          isDefaultImage={isTalentDefaultProfileImage}
        />
        {/* <div style={{ position: 'relative', cursor: 'pointer' }}>
          <IcoBell />
          <div style={{ position: 'absolute', top: '-2px', right: '-2px', width: 8, height: 8, borderRadius: '50%', background: 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)' }} />
        </div> */}
      </div>
      <div style={{ borderTop: `1px solid ${C.g200}`, margin: '12px 0' }} />
      <div style={{ padding: '10px 24px' }}>
        <div style={{ background: C.g200, borderRadius: '10px', padding: '5px', display: 'flex', position: 'relative' }}>
          {isProducer && <div style={{ position: 'absolute', left: 5, top: 5, width: 'calc(50% - 5px)', height: '46px', background: C.white, borderRadius: '6px', boxShadow: C.shadow }} />}
          <button style={{ flex: 1, height: '46px', background: 'transparent', border: 'none', cursor: 'default', ...t('18px', 600, C.bk), position: 'relative', zIndex: 1 }}>배우</button>
          {isProducer ? (
            <button
              onClick={handleSwitchToProducer}
              style={{ flex: 1, height: '46px', background: 'transparent', border: 'none', cursor: 'pointer', ...t('18px', 600, C.sub), position: 'relative', zIndex: 1 }}
            >제작사</button>
          ) : (
            <button
              onClick={() => navigate('/talent/settings')}
              style={{ flex: 1, height: '46px', background: 'transparent', border: 'none', cursor: 'pointer', ...t('18px', 600, C.g300), position: 'relative', zIndex: 1 }}
            >제작사</button>
          )}
        </div>
      </div>
      <nav style={{ padding: '10px 24px', flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '5px' }}>
        {MENU.map(({ id, label, path, iconSrc, comingSoon }) => {
          const active = id === activeId;
          return (
            <button key={id} onClick={() => navigate(path)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '22px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: active ? C.bk : 'transparent', width: '100%', textAlign: 'left', flexShrink: 0 }}>
              <MenuIcon src={iconSrc} color={active ? C.white : C.g400} />
              <span style={t('18px', 600, active ? C.white : C.g400)}>{label}</span>
              {comingSoon && (
                <span style={{
                  marginLeft: 'auto', padding: '2px 8px', borderRadius: '100px',
                  background: active ? 'rgba(255,255,255,0.18)' : C.g200,
                  ...t('11px', 600, active ? C.white : C.g400),
                }}>
                  준비 중
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div style={{ borderTop: `1px solid ${C.g200}`, padding: '20px 24px', flexShrink: 0 }}>
        <button onClick={() => setShowLogoutConfirm(true)} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <IcoLogout />
          <span style={t('18px', 600, C.g400)}>로그아웃</span>
        </button>
      </div>
      {showLogoutConfirm && (
        <LogoutConfirmModal
          onConfirm={async () => { await logout(); window.location.replace('/'); }}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </aside>
    </>
  );
}

export default Sidebar;
